import React, { useMemo, useState } from 'react'
import Dropdown from 'react-bootstrap/Dropdown'
import styled from 'styled-components/macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronDown,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons'
import palette from 'src/designSystem/variables/palette'
import {
  FilterDropdownToggle,
  FilterDropdownToggleContent,
} from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.styles'
import {
  DropDownMenuWithNesting,
  FilterGroupHeading,
  FilterOptionContainer,
  FilterItemsContainer,
} from 'src/components/Reusable/IndexPage/FilterBar/FilterDropdowns/FilterDropdown.styles'
import { DropdownItemType } from 'src/components/Reusable/IndexPage/FilterBar/FilterDropdowns/FilterDropdown.constants'
import { CheckBoxInput } from 'src/components/Reusable/Inputs/CheckBox'
import { CountChip } from 'src/components/Reusable/Chips/BasicChip'
import { MenuPlaceholder } from 'src/components/Placeholders/MenuPlaceholder'
import { DropdownSearchInput } from 'src/components/Reusable/Inputs/DropdownSearchInput/DropdownSearchInput'

const GroupRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;

  .form-check {
    flex: 1;
    min-width: 0;
  }
`

const ExpandGroupButton = styled.button`
  border: none;
  background: transparent;
  padding: 2px 6px;
  color: ${palette.neutral['600']};
  cursor: pointer;
`

interface LocalFilterDropdownProps {
  filterName: string
  filterValues: DropdownItemType[]
  isLoading?: boolean
  selectedValues: string[]
  onChangeSelectedValues: (values: string[]) => void
  searchPlaceholder?: string
  noMatchesText?: string
  // Items with a nestedFilter are options themselves, with their children in a
  // collapsible list under them, rather than plain headings.
  selectableGroups?: boolean
}

/**
 * A filter dropdown that keeps its selection in the caller's state rather than the page
 * URL, for filters inside a modal. Values with a nestedFilter render as a heading over
 * their own checkboxes.
 */
export const LocalFilterDropdown: React.FC<LocalFilterDropdownProps> = ({
  filterName,
  filterValues,
  isLoading,
  selectedValues,
  onChangeSelectedValues,
  searchPlaceholder = 'Search...',
  noMatchesText = 'No matches found.',
  selectableGroups = false,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues])
  const count = selectedValues.length

  const handleToggle = (nextShow: boolean) => {
    setIsOpen(nextShow)
    if (!nextShow) {
      setSearchQuery('')
    }
  }

  const toggleValue = (val: string) => {
    if (selectedSet.has(val)) {
      onChangeSelectedValues(selectedValues.filter((id) => id !== val))
    } else {
      onChangeSelectedValues([...selectedValues, val])
    }
  }

  const hasNested = filterValues.some((fv) => !!fv.nestedFilter)
  // Long requirement names under a framework need more room than plain options.
  const menuMaxWidth = selectableGroups ? '480px' : '320px'

  // Selectable groups (e.g. a framework over its requirements) start collapsed; a
  // search shows every group that matches.
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const isSearching = !!searchQuery.trim()
  const toggleExpanded = (value: string) =>
    setExpanded((previous) => {
      const next = new Set(previous)
      next.has(value) ? next.delete(value) : next.add(value)
      return next
    })

  const renderChildren = (items: DropdownItemType[]) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {items.map((subItem) => (
        <div
          key={subItem.value}
          style={{ padding: '2px 0' }}
          onClick={(e) => e.stopPropagation()}
        >
          <CheckBoxInput
            id={`${filterName}-${subItem.value}`}
            label={subItem.label as React.ReactNode}
            checked={selectedSet.has(subItem.value)}
            onChange={() => toggleValue(subItem.value)}
            onClick={(e) => e.stopPropagation()}
            tabIndex={-1}
          />
        </div>
      ))}
    </div>
  )

  const filteredValues = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return filterValues

    return filterValues
      .map((fv) => {
        if (fv.nestedFilter) {
          const matchingChildren = fv.nestedFilter.filterValues.filter(
            (child) =>
              typeof child.label === 'string'
                ? child.label.toLowerCase().includes(query)
                : false,
          )
          const categoryMatches =
            typeof fv.label === 'string' &&
            fv.label.toLowerCase().includes(query)

          if (categoryMatches) {
            return fv
          }

          if (matchingChildren.length > 0) {
            return {
              ...fv,
              nestedFilter: {
                ...fv.nestedFilter,
                filterValues: matchingChildren,
              },
            }
          }

          return null
        }

        const labelStr =
          typeof fv.label === 'string' ? fv.label.toLowerCase() : ''
        return labelStr.includes(query) ? fv : null
      })
      .filter((fv): fv is DropdownItemType => fv !== null)
  }, [filterValues, searchQuery])

  return (
    <div>
      <Dropdown show={isOpen} onToggle={handleToggle} autoClose="outside">
        <Dropdown.Toggle
          as={FilterDropdownToggle}
          type="button"
          id={`${filterName}-local-dropdown`}
          $hasSelection={count > 0}
        >
          <FilterDropdownToggleContent>
            <p>{filterName}</p>
            {count > 0 && <CountChip ml="xxs">{count}</CountChip>}
          </FilterDropdownToggleContent>
        </Dropdown.Toggle>
        {isLoading ? (
          <Dropdown.Menu rootCloseEvent="mousedown">
            <MenuPlaceholder height={108} />
          </Dropdown.Menu>
        ) : (
          <DropDownMenuWithNesting
            $hasNested={hasNested}
            rootCloseEvent="mousedown"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
            style={{
              zIndex: 1055,
              maxWidth: menuMaxWidth,
              minWidth: '260px',
              width: 'max-content',
            }}
          >
            <DropdownSearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={searchPlaceholder}
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
            <FilterItemsContainer
              style={{
                padding: '8px 12px',
                maxHeight: '280px',
                overflowY: 'auto',
                maxWidth: menuMaxWidth,
              }}
            >
              {filteredValues.length === 0 ? (
                <p
                  style={{
                    margin: 0,
                    padding: '8px',
                    color: '#6c757d',
                    fontSize: '13px',
                  }}
                >
                  {noMatchesText}
                </p>
              ) : (
                filteredValues.map((fv) => {
                  if (fv.nestedFilter && selectableGroups) {
                    const isExpanded = isSearching || expanded.has(fv.value)
                    const selectedChildren =
                      fv.nestedFilter.filterValues.filter((child) =>
                        selectedSet.has(child.value),
                      ).length
                    return (
                      <FilterOptionContainer
                        key={fv.value}
                        style={{ marginBottom: '4px' }}
                      >
                        <GroupRow onClick={(e) => e.stopPropagation()}>
                          <CheckBoxInput
                            id={`${filterName}-${fv.value}`}
                            label={fv.label as React.ReactNode}
                            checked={selectedSet.has(fv.value)}
                            onChange={() => toggleValue(fv.value)}
                            onClick={(e) => e.stopPropagation()}
                            tabIndex={-1}
                          />
                          {!isExpanded && selectedChildren > 0 && (
                            <CountChip>{selectedChildren}</CountChip>
                          )}
                          {!isSearching && (
                            <ExpandGroupButton
                              type="button"
                              aria-expanded={isExpanded}
                              aria-label={`${isExpanded ? 'Hide' : 'Show'} ${
                                fv.label
                              } items`}
                              onClick={(e) => {
                                e.stopPropagation()
                                toggleExpanded(fv.value)
                              }}
                            >
                              <FontAwesomeIcon
                                icon={
                                  isExpanded ? faChevronDown : faChevronRight
                                }
                              />
                            </ExpandGroupButton>
                          )}
                        </GroupRow>
                        {isExpanded && (
                          <div style={{ paddingLeft: '24px' }}>
                            {renderChildren(fv.nestedFilter.filterValues)}
                          </div>
                        )}
                      </FilterOptionContainer>
                    )
                  }

                  if (fv.nestedFilter) {
                    return (
                      <FilterOptionContainer
                        key={fv.value}
                        style={{ marginBottom: '12px' }}
                      >
                        <FilterGroupHeading>{fv.label}</FilterGroupHeading>
                        {renderChildren(fv.nestedFilter.filterValues)}
                      </FilterOptionContainer>
                    )
                  }

                  const isChecked = selectedSet.has(fv.value)
                  return (
                    <FilterOptionContainer
                      key={fv.value}
                      style={{ marginBottom: '4px' }}
                    >
                      <div
                        style={{ padding: '2px 0' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <CheckBoxInput
                          id={`${filterName}-${fv.value}`}
                          label={fv.label as React.ReactNode}
                          checked={isChecked}
                          onChange={() => toggleValue(fv.value)}
                          onClick={(e) => e.stopPropagation()}
                          tabIndex={-1}
                        />
                      </div>
                    </FilterOptionContainer>
                  )
                })
              )}
            </FilterItemsContainer>
          </DropDownMenuWithNesting>
        )}
      </Dropdown>
    </div>
  )
}
